// frontend/src/services/glossary/glossaryService.js
import api from '../api';
import errorService from '../error/errorService';

/**
 * Glossary Service - Handles all glossary-related operations
 */
class GlossaryService {
  /**
   * Get glossary for a project
   * @param {string} projectId - The ID of the project
   * @returns {Promise<Array>} - Promise that resolves with the glossary terms
   */
  async getGlossary(projectId) {
    try {
      const response = await api.get(`/projects/${projectId}/glossary`);
      return response.data?.terms || [];
    } catch (error) {
      return errorService.handleApiError(error, 'Failed to fetch glossary');
    }
  }

  /**
   * Upload a new glossary
   * @param {string} projectId - The ID of the project
   * @param {FormData} formData - Form data with the glossary file
   * @returns {Promise<Object>} - Promise that resolves with the uploaded glossary
   */
  async uploadGlossary(projectId, formData) {
    try {
      const response = await api.post(`/projects/${projectId}/glossary`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      return response.data;
    } catch (error) {
      return errorService.handleApiError(error, 'Failed to upload glossary');
    }
  }

  /**
   * Optimize a glossary for a specific text
   * @param {Array} glossary - The original glossary terms
   * @param {string} text - The text to optimize for
   * @param {string} language - The source language code
   * @returns {Array} - The optimized glossary
   */
  optimizeGlossary(glossary, text, language = 'en') {
    if (!text || !glossary || glossary.length <= 60) {
      // Don't optimize small glossaries or when no text is provided
      return glossary;
    }
    
    try {
      // Create a set of all words in the text (lowercase)
      const textWords = new Set(
        text.toLowerCase()
            .split(/\s+/)
            .filter(word => word.length > 2)
      );
      
      // Filter glossary terms that have words appearing in the text
      const optimizedGlossary = glossary.filter(term => {
        // Skip empty or invalid terms
        if (!term.source) return false;
        
        // Split source term into words
        const termWords = term.source.toLowerCase().split(/\s+/);
        
        // Check if any word in the term appears in the text
        return termWords.some(word => textWords.has(word));
      });
      
      // If optimization is too aggressive, use the original glossary
      if (optimizedGlossary.length < glossary.length * 0.1) {
        return glossary;
      }
      
      return optimizedGlossary;
    } catch (error) {
      console.error("Error optimizing glossary:", error);
      // Return the original glossary if optimization fails
      return glossary;
    }
  }
}

export default new GlossaryService();